import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import {
  HttpClientModule,
  HttpClient,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";
import { RouteReuseStrategy } from "@angular/router";
import { DatePipe } from "@angular/common";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { IonicModule, IonicRouteStrategy, NavParams } from "@ionic/angular";

import { VgApiService, VgCoreModule } from "@videogular/ngx-videogular/core";
import { VgControlsModule } from "@videogular/ngx-videogular/controls";
import { VgBufferingModule } from "@videogular/ngx-videogular/buffering";
import { VgStreamingModule } from "@videogular/ngx-videogular/streaming";
import { VgOverlayPlayModule } from "@videogular/ngx-videogular/overlay-play";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { AppHeaderComponent } from "./header/app-header/app-header.component";
import { Global } from "../global";
import { Storage } from "@ionic/storage-angular";
import { IonicStorageModule } from "@ionic/storage-angular";
import { JwtModule, JWT_OPTIONS, JwtHelperService } from "@auth0/angular-jwt";
import { MenuitemPageModule } from "src/app/lessons/modals/menuitem/menuitem.module";
import { ModalPageModule } from "src/app/modals/modal/modal.module";
import { ProcedureModalPageModule } from "src/app/modals/procedure-modal/procedure-modal.module";
import { DatepickerModalPageModule } from "src/app/modals/datepicker-modal/datepicker-modal.module";
import { PeopleModalPageModule } from "src/app/modals/people-modal/people-modal.module";
import { EquipmentModalPageModule } from "src/app/modals/equipment-modal/equipment-modal.module";
import { LessonModalPageModule } from "src/app/modals/lesson-modal/lesson-modal.module";
import { VehicleModalPageModule } from "src/app/modals/vehicle-modal/vehicle-modal.module";
import { ProcedureSelectionModalPageModule } from "src/app/modals/procedureSelection-modal/procedureSelection-modal.module";
import { MediaSelectionModalPageModule } from "src/app/modals/mediaSelection-modal/mediaSelection-modal.module";
import { LessonParameterModalPageModule } from "src/app/modals/lessonParameter-modal/lessonParameter-modal.module";
import { SchedulePeopleModalPageModule } from "src/app/modals/schedulePeople-modal/schedulePeople-modal.module";
import { QuestionModalPageModule } from "src/app/modals/question-modal/question-modal.module";
import { RegistrationPageModule } from "src/app/login/registration/registrationModal.module";
import { ModalComponent } from "./modals/modal/modal.component";
import { IonicSelectableComponent } from "ionic-selectable";
import { catchError, map } from "rxjs/operators";
import { Observable, ObservableInput, of } from "rxjs";
import { APP_INITIALIZER } from "@angular/core";
import { AppConfig } from "./app.config";
import { TelerikReportingModule } from "@progress/telerik-angular-report-viewer";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { ServiceInjectorModule } from "./modules/service-injector.module";
import { NotificationService } from "./modals/modal/components/notifications/notifications.service";
import { EditLessonComponentModule } from "./modals/editLesson_modal/editLesson.module";

// Required for MSAL
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from "@azure/msal-browser";
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from "@azure/msal-angular";

const isIE =
  window.navigator.userAgent.indexOf("MSIE ") > -1 ||
  window.navigator.userAgent.indexOf("Trident/") > -1;

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

// export function jwtOptionsFactory(storage) {
//   return {
//     tokenGetter: () => {
//       return storage.get("access_token");
//     },
//     whitelistedDomains: ["localhost:5000"],
//   };
// }
export function jwtOptionsFactory() {
  return {
    tokenGetter: () => localStorage.getItem("access_token"),
    allowedDomains: ["your-api.com"],
    disallowedRoutes: ["your-api.com/auth/"],
  };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  
  return new PublicClientApplication({
    auth: {
  
      clientId: "b84f5b7c-03c7-450f-a2fd-45eeb84d226d",

      // Work/school Microsoft accounts only
      authority: "https://login.microsoftonline.com/organizations",

      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ["User.Read"],
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  
  protectedResourceMap.set("https://graph.microsoft.com/v1.0/me", [
    "User.Read",
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

@NgModule({
  declarations: [AppComponent, AppHeaderComponent, ModalComponent],
  //entryComponents: [ModalComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    IonicModule.forRoot(),
    AppRoutingModule,
    MenuitemPageModule,
    ModalPageModule,
    ProcedureModalPageModule,
    ProcedureSelectionModalPageModule,
    LessonParameterModalPageModule,
    MediaSelectionModalPageModule,
    DatepickerModalPageModule,
    PeopleModalPageModule,
    SchedulePeopleModalPageModule,
    EquipmentModalPageModule,
    LessonModalPageModule,
    VehicleModalPageModule,
    QuestionModalPageModule,
    RegistrationPageModule,
    IonicSelectableComponent,
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    VgStreamingModule,
    ScrollingModule,
    HttpClientModule,
    TelerikReportingModule,
    ServiceInjectorModule,
    EditLessonComponentModule,
    IonicStorageModule.forRoot({
      // name: "__XRSdb",
      // driverOrder: ["indexeddb", "sqlite", "websql"],
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        //deps: [Storage],
      },
    }),
  ],
  providers: [
    DatePipe,
    Global,
    VgApiService,
    NotificationService,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    AppConfig,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfig],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
